import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
    IApiResponse,
    IAuthorizationSettings,
    ICheckUserWithCaptchaResponse,
    ICuratorRegistration,
    IDirectoryAgreement,
    IDirectoryUserBlockInn,
    IRationalizatorRegistration,
    IRatsorgRegistration,
    ISMSCodeConfirmResponse,
    ISMSCodeSendResponse,
    IUserAgreementResponse,
    IUserAuthorizationResponse,
    IUserRegistrationByPhoneResponse,
    IUserShortDto,
    NeededAcceptUserAgreements,
    IPasswordRule,
    ISetPasswordResponse
} from '../models/domain';
import {AgreementType} from '../models/enums';


@Injectable({
    providedIn: 'root'
})
export class AccountService {

    private _httpClient: HttpClient;
    private _serverInternalError: string;

    constructor(
        private _translateService: TranslateService,
        @Inject(HttpClient) httpClient: HttpClient) {

        this._httpClient = httpClient;
        this.initTranslations();
    }

    private initTranslations() {
        this._translateService.get(
                [
                    'Common.ServerInternalError'
                ])
            .subscribe(
                result => {
                    this._serverInternalError = result['Common.ServerInternalError'];
                });
    }

    private catchErrorInternal<T>(source$: Observable<T>): Observable<T> {
        return source$
            .pipe(
                catchError((httpErrorResponse: HttpErrorResponse) => {
                    // TODO check status (400, 500, ...) and generate response
                    return of({
                        error: this._serverInternalError,
                        data: {
                        }
                    } as any as T);
            }));
    }


    public userRegistrationByPhone_SendCode(phoneValue: string, captchaCode: string): Observable<IUserRegistrationByPhoneResponse> {
        const body = {
            phone: phoneValue,
            captchaCode: captchaCode
        };

        return this._httpClient
            .post<IUserRegistrationByPhoneResponse>('./api/v1/Account/registration-by-phone/send-code', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public userRegistrationByPhone_ConfirmCode(phoneValue: string, codeValue: string): Observable<IUserRegistrationByPhoneResponse> {
        const body = {
            phone: phoneValue,
            code: codeValue
        };

        return this._httpClient
            .post<IUserRegistrationByPhoneResponse>('./api/v1/Account/registration-by-phone/confirm-code', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public userRegistrationByPhone_SetPassword(phoneValue: string, passwordValue: string): Observable<IUserRegistrationByPhoneResponse> {
        const body = {
            phone: phoneValue,
            password: passwordValue
        };

        return this._httpClient
            .post<IUserRegistrationByPhoneResponse>('./api/v1/Account/registration-by-phone/set-password', body)
            .pipe(this.catchErrorInternal.bind(this));
    }


    public login(
        login: string,
        passwordValue: string,
        clientId: string,
        captchaCode: string): Observable<IUserAuthorizationResponse> {

        const body = {
            login: login,
            password: passwordValue,
            clientId: clientId,
            captchaCode: captchaCode
        };

        return this._httpClient
            .post<IUserAuthorizationResponse>('./api/v1/Account/login', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public loginByPhone(
        phone: string,
        passwordValue: string,
        clientId: string,
        captchaCode: string): Observable<IUserAuthorizationResponse> {

        const body = {
            login: phone,
            password: passwordValue,
            clientId: clientId,
            captchaCode: captchaCode
        };

        return this._httpClient
            .post<IUserAuthorizationResponse>('./api/v1/Account/login-by-phone', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public loginByIdap(
        login: string,
        passwordValue: string,
        clientId: string,
        captchaCode: string
    ): Observable<IUserAuthorizationResponse> {

        const body = {
            login: login,
            password: passwordValue,
            clientId: clientId,
            captchaCode: captchaCode
        };

        return this._httpClient
            .post<IUserAuthorizationResponse>('./api/v1/Account/login-by-ldap', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public changePassword_IsPasswordValid(
        userIdValue: number,
        oldPasswordValue: string,
        captchaCode: string): Observable<ICheckUserWithCaptchaResponse> {

        const body = {
            userId: userIdValue,
            password: oldPasswordValue,
            captchaCode: captchaCode
        };

        return this._httpClient
            .post<ICheckUserWithCaptchaResponse>('./api/v1/Account/change-password/is-password-valid', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public changePassword_SendSMSCode(
            phoneValue: string,
            captchaCode: string): Observable<ISMSCodeSendResponse> {
        const body = {
            phone: phoneValue,
            captchaCode: captchaCode
        };

        return this._httpClient
            .post<ISMSCodeSendResponse>('./api/v1/Account/change-password/send-sms-code', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public changePassword_ConfirmSMSCode(phoneValue: string, codeValue: string): Observable<ISMSCodeConfirmResponse> {
        const body = {
            phone: phoneValue,
            code: codeValue
        };

        return this._httpClient
            .post<ISMSCodeConfirmResponse>('./api/v1/Account/change-password/confirm-sms-code', body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public changePassword_SetPassword(ticket: string, newPasswordValue: string): Observable<ISetPasswordResponse> {
        const body = {
            ticket,
            newPassword: newPasswordValue
        };

        return this._httpClient
            .post<ISetPasswordResponse>('./api/v1/Account/change-password/set-password', body)
            .pipe(this.catchErrorInternal.bind(this));
    }


    public userAgreement_Get(agreementType: AgreementType): Observable<IDirectoryAgreement> {
        return this._httpClient
            .post<IDirectoryAgreement>('./api/v1/Account/user-agreement', agreementType)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public userAgreement_Accept(agreementId: number, userTicket: string, isAuthorized: boolean = false): Observable<NeededAcceptUserAgreements> {
        const body = {
            agreementId: agreementId,
            userTicket: userTicket,
        };

        let url = isAuthorized ? './api/v1/Account/user-agreement/accept-authorized' : './api/v1/Account/user-agreement/accept';

        return this._httpClient
            .post<NeededAcceptUserAgreements>(url, body)
            .pipe(this.catchErrorInternal.bind(this));
    }

    public curatorRegistration(item: ICuratorRegistration): Observable<IApiResponse>{
        return this._httpClient
            .post('./api/v1/Account/curator-registration', item)
            .pipe(this.catchErrorInternal.bind(this))
    }

    public ratsorgRegistration(item: FormData): Observable<IApiResponse>{
        return this._httpClient
            .post('./api/v1/Account/ratsorg-registration', item, { })
            .pipe(this.catchErrorInternal.bind(this))
    }

    public getRatsorg(id: number): Observable<IUserShortDto[]>{
        return this._httpClient
            .post('./api/v1/Account/get-all-ratsorg',id )
            .pipe(this.catchErrorInternal.bind(this))
    }

    public rationalizatorRegistration(item: IRationalizatorRegistration): Observable<IApiResponse>{
        return this._httpClient
            .post('./api/v1/Account/rationalizator-registration', item, { })
            .pipe(this.catchErrorInternal.bind(this))
    }

    public isCaptchaNeeded(): Observable<boolean> {
        return this._httpClient.get<boolean>('./api/v1/Account/captcha-needed');
    }
}
